import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Card, CardBody, Badge, Button } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import KeeperLoginModal from './KeeperLoginModal'
import KeeperReconTable from './KeeperReconTable'
import UnmappedCompaniesModal from './UnmappedCompaniesModal'
import {
  fetchReport,
  fetchCompanyLabels,
  fetchUnmappedCompanies,
  fetchMappings,
  executeRecon,
  startKeeperSync,
  submit2faCode,
  submitSsoToken,
  resetKeeperSync,
} from '../../actions/keeperReconActions'

const KeeperReconInner = () => {
  const dispatch = useDispatch()
  const [syncModalOpen, setSyncModalOpen] = useState(false)
  const [unmappedModalOpen, setUnmappedModalOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchReport())
    dispatch(fetchCompanyLabels())
    dispatch(fetchUnmappedCompanies())
    dispatch(fetchMappings())
  }, [dispatch])

  const { loading, report, lastSyncDate, executing, unmappedCompanies } = useSelector((state) => state.keeperRecon)
  const syncState = useSelector((state) => ({
    syncId: state.keeperRecon.syncId,
    status: state.keeperRecon.syncStatus,
    prompt: state.keeperRecon.prompt,
    ssoLoginUrl: state.keeperRecon.ssoLoginUrl,
    progress: state.keeperRecon.progress,
    error: state.keeperRecon.syncError,
    loading: state.keeperRecon.syncLoading,
  }))

  const formattedLastSync = React.useMemo(() => {
    if (!lastSyncDate) return null
    return new Date(lastSyncDate).toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }, [lastSyncDate])

  // Filter out orphaned/unmapped rows — dashboard only shows CW companies
  const dashboardRows = React.useMemo(() => {
    if (!report) return []
    return report.filter((r) => r.companyId != null)
  }, [report])

  const statusCounts = React.useMemo(() => {
    const counts = { total: 0, completed: 0, pending: 0, flagged: 0, blocked: 0, noChanges: 0, approved: 0, awaitingSync: 0 }
    dashboardRows.forEach((r) => {
      counts.total++
      switch (r.status) {
        case 'Completed': counts.completed++; break
        case 'Pending': counts.pending++; break
        case 'Flagged': counts.flagged++; break
        case 'Blocked': counts.blocked++; break
        case 'NoChanges': counts.noChanges++; break
        case 'Approved': counts.approved++; break
        case 'AwaitingSync': counts.awaitingSync++; break
        default: break
      }
    })
    return counts
  }, [dashboardRows])

  const unmappedCount = (unmappedCompanies || []).length

  const toggleSyncModal = () => {
    if (syncModalOpen) {
      dispatch(resetKeeperSync())
    }
    setSyncModalOpen(!syncModalOpen)
  }

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-4">
          <Col>
            <PageHeading>Keeper Reconciliation</PageHeading>
            {formattedLastSync && (
              <small className="text-muted">Data last synced on {formattedLastSync}</small>
            )}
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            <button
              className="btn btn-outline-primary mr-2"
              onClick={toggleSyncModal}
            >
              Sync from Keeper
            </button>
            {unmappedCount > 0 && (
              <button
                className="btn btn-outline-secondary mr-2"
                onClick={() => setUnmappedModalOpen(true)}
              >
                Unmapped Keeper Companies <Badge color="primary" className="ml-1">{unmappedCount}</Badge>
              </button>
            )}
            <button
              className="btn btn-primary"
              disabled={executing || loading}
              onClick={() => dispatch(executeRecon())}
            >
              {executing ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" />
                  Executing...
                </>
              ) : (
                'Execute Auto-Recon'
              )}
            </button>
          </Col>
        </Row>

        {/* Summary cards */}
        <Row className="mb-4">
          <Col md={2}>
            <Card>
              <CardBody className="text-center">
                <h4 className="mb-0">{statusCounts.total}</h4>
                <small className="text-muted">Total</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-success">
              <CardBody className="text-center">
                <h4 className="mb-0 text-success">{statusCounts.completed}</h4>
                <small className="text-muted">Completed</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-primary">
              <CardBody className="text-center">
                <h4 className="mb-0 text-primary">{statusCounts.pending + statusCounts.noChanges + statusCounts.approved}</h4>
                <small className="text-muted">Ready</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-warning">
              <CardBody className="text-center">
                <h4 className="mb-0 text-warning">{statusCounts.flagged}</h4>
                <small className="text-muted">Flagged</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="border-danger">
              <CardBody className="text-center">
                <h4 className="mb-0 text-danger">{statusCounts.blocked}</h4>
                <small className="text-muted">Blocked</small>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={{ borderColor: '#adb5bd' }}>
              <CardBody className="text-center">
                <h4 className="mb-0" style={{ color: '#adb5bd' }}>{statusCounts.awaitingSync}</h4>
                <small className="text-muted">Awaiting Sync</small>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <KeeperReconTable rows={dashboardRows} />
        )}
      </Container>

      <KeeperLoginModal
        isOpen={syncModalOpen}
        toggle={toggleSyncModal}
        syncState={syncState}
        onStartSync={(email, password) => dispatch(startKeeperSync(email, password))}
        onSubmit2fa={(code) => dispatch(submit2faCode(syncState.syncId, code))}
        onSubmitSsoToken={(token) => dispatch(submitSsoToken(syncState.syncId, token))}
      />

      <UnmappedCompaniesModal
        isOpen={unmappedModalOpen}
        toggle={() => setUnmappedModalOpen(false)}
      />
    </PageContainer>
  )
}

const KeeperRecon = () => {
  return (
    <AzureAD provider={authProvider} reduxStore={store}>
      {({ accountInfo }) => {
        const roles = accountInfo?.account?.idToken?.roles || []
        const isAuthorized = roles.includes('Admin') || roles.includes('Sales')

        if (!isAuthorized) {
          return (
            <PageContainer>
              <Container fluid className="mt-3">
                <p>You do not have permission to view this page.</p>
              </Container>
            </PageContainer>
          )
        }

        return <KeeperReconInner />
      }}
    </AzureAD>
  )
}

export default KeeperRecon
